import React, { useEffect, useState } from 'react';
import CreateStorePopup from './UploadPage/CreateStorePopup.js';
import UploadPopup from './UploadPage/UploadPopup.js';
import RemoveConfirmationModal from './UploadPage/RemoveConfirmationModal.js';
import useAuth from './useAuth.js';
import useStoreActions from './UploadPage/StoreActions/StoreActions.js';
import Header from './Header.js';
import StoreList from './UploadPage/StoreList.js';
import ActionButtons from './UploadPage/ActionButtons.js';
import StoreItems from './UploadPage/StoreItems.js';
import ItemPreview from './UploadPage/ItemPreview.js';
import '../App.css';

const UploadPage = () => {
  // ✅ Only get user from useAuth (no stores)
  const { user } = useAuth();

  // ✅ Manage store state directly in this component
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  // ✅ Get store actions (which will now load from new /stores structure)
  const {
    error,
    setError,
    successMessage,
    setSuccessMessage,
    showCreateStorePopup,
    setShowCreateStorePopup,
    showUploadPopup,
    setShowUploadPopup,
    showRemoveModal,
    setShowRemoveModal,
    itemToRemove,
    setItemToRemove,
    handleCreateStore,
    handleUpload,
    handleRemoveStore,
    handleRemoveItem,
    handleLogout,
    copyLinkToClipboard,
    generateItemShareLink,
    editItem,
    setEditItem,
    isGenerating,
    generationProgress,
    generationStatus,
    isDownloadingInBackground,
    uploadingItems,
    loadUserStores, // ✅ Now we can access this function
  } = useStoreActions(stores, setStores, selectedStore, setSelectedStore, items, setItems, selectedItem, setSelectedItem);

  // ✅ Load stores when user is available
  useEffect(() => {
    if (user) {
      console.log('🔍 User available, loading stores from NEW structure...');
      loadUserStores();
    }
  }, [user]);

  // Auto-select first store when stores are loaded
  useEffect(() => {
    if (stores.length > 0 && !selectedStore) {
      console.log('🎯 Auto-selecting first store:', stores[0].name);
      setSelectedStore(stores[0]);
      setItems(stores[0].items || []);
    }
  }, [stores, selectedStore]);

  // Auto-select first item when items are loaded
  useEffect(() => {
    if (items.length > 0 && !selectedItem) {
      console.log('🎯 Auto-selecting first item:', items[0].itemName);
      setSelectedItem(items[0]);
    }
  }, [items, selectedItem]);

  if (stores.length === 0) {
    return (
      <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh', padding: '20px' }}>
        <Header />
        <div style={{ textAlign: 'right', marginBottom: '20px' }}>
          <button
            onClick={() => setShowCreateStorePopup(true)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '20px',
              backgroundColor: '#E6E6FA',
              color: '#2D3E50',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '18px', lineHeight: '1' }}>+</span> New Store
          </button>
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#666', fontSize: '16px' }}>
            No stores yet. Create a new store to get started.
          </p>
          {isDownloadingInBackground && (
            <p style={{ color: '#666', fontSize: '14px' }}>
              📥 Processing background downloads...
            </p>
          )}
        </div>
        {showCreateStorePopup && (
          <CreateStorePopup
            onClose={() => setShowCreateStorePopup(false)}
            onCreate={(storeName) => handleCreateStore(storeName, user)}
          />
        )}
        {error && <p style={{ color: 'red', textAlign: 'center', marginTop: '20px' }}>{error}</p>}
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh', padding: '20px' }}>
      <Header />
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <StoreList
          stores={stores}
          setStores={setStores}
          selectedStore={selectedStore}
          setSelectedStore={setSelectedStore}
          setItems={setItems}
          setSelectedItem={setSelectedItem}
          setShowRemoveModal={setShowRemoveModal}
          setItemToRemove={setItemToRemove}
          copyLinkToClipboard={copyLinkToClipboard}
        />
        <ActionButtons
          setShowCreateStorePopup={setShowCreateStorePopup}
          setShowUploadPopup={setShowUploadPopup}
        />
      </div>
      {selectedStore && (
        <div style={{ display: 'flex', gap: '20px' }}>
          <StoreItems
            items={items}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            setShowRemoveModal={setShowRemoveModal}
            setItemToRemove={setItemToRemove}
            setShowUploadPopup={setShowUploadPopup}
            setEditItem={setEditItem}
            uploadingItems={uploadingItems}
          />
          <ItemPreview
            selectedStore={selectedStore}
            items={items}
            selectedItem={selectedItem}
          />
        </div>
      )}
      
      {/* Show generation progress if AI is generating */}
      {isGenerating && (
        <div style={{ 
          position: 'fixed', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
          zIndex: 1000
        }}>
          <div style={{ textAlign: 'center' }}>
            <p>🤖 Generating 3D Model...</p>
            <div style={{ 
              width: '200px', 
              height: '10px', 
              backgroundColor: '#f0f0f0', 
              borderRadius: '5px',
              marginBottom: '10px'
            }}>
              <div style={{ 
                width: `${generationProgress}%`, 
                height: '100%', 
                backgroundColor: '#8A63FF', 
                borderRadius: '5px',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
            <p style={{ fontSize: '14px', color: '#666' }}>{generationStatus}</p>
          </div>
        </div>
      )}

      {showCreateStorePopup && (
        <CreateStorePopup
          onClose={() => setShowCreateStorePopup(false)}
          onCreate={(storeName) => handleCreateStore(storeName, user)}
        />
      )}
      {showUploadPopup && selectedStore && (
        <UploadPopup
          onClose={() => {
            setShowUploadPopup(false);
            setEditItem(null);
          }}
          onUpload={handleUpload}
          editItem={editItem}
        />
      )}
      {showRemoveModal && itemToRemove && (
        <RemoveConfirmationModal
          onClose={() => {
            setShowRemoveModal(false);
            setItemToRemove(null);
          }}
          onConfirm={stores.find(store => store.id === itemToRemove.id) ? handleRemoveStore : handleRemoveItem}
          itemName={itemToRemove.name}
        />
      )}
      {error && <p style={{ color: 'red', textAlign: 'center', marginTop: '20px' }}>{error}</p>}
      {successMessage && (
        <p style={{ color: 'green', textAlign: 'center', marginTop: '20px' }}>
          {successMessage}
        </p>
      )}
    </div>
  );
};

export default UploadPage;